(function () {
  'use strict';

  var slugify = function (value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  var collectionKey = function (url) {
    return String(url || '')
      .split('?')[0]
      .split('/')
      .filter(Boolean)
      .pop() || '';
  };

  var finiteYear = function (value) {
    var year = Number(value);
    return Number.isFinite(year) && year >= 1900 && year <= 2200 ? year : null;
  };

  var unique = function (items) {
    return Array.from(new Set(items));
  };

  var HarmonizeFitmentGuide = function (root) {
    this.root = root;
    this.form = root.querySelector('[data-fitment-form]');
    this.makeSelect = root.querySelector('[data-fitment-make]');
    this.modelSelect = root.querySelector('[data-fitment-model]');
    this.yearSelect = root.querySelector('[data-fitment-year]');
    this.submitButton = root.querySelector('[data-fitment-submit]');
    this.resetButton = root.querySelector('[data-fitment-reset]');
    this.status = root.querySelector('[data-fitment-status]');
    this.results = root.querySelector('[data-fitment-results]');
    this.resultHeading = root.querySelector('[data-fitment-result-heading]');
    this.resultCount = root.querySelector('[data-fitment-result-count]');
    this.grid = root.querySelector('[data-fitment-grid]');
    this.empty = root.querySelector('[data-fitment-empty]');
    this.modelLink = root.querySelector('[data-fitment-model-link]');
    this.records = [];
    this.modelsByMake = new Map();
    this.templateByHandle = new Map();
    this.modelOptions = [];
    this.selectedModel = null;
    this.lastResultsKey = '';
  };

  HarmonizeFitmentGuide.prototype.init = function () {
    if (this.root.dataset.fitmentInitialized === 'true') return;
    this.root.dataset.fitmentInitialized = 'true';

    this.records = this.readRecords();
    this.templateByHandle = this.readProductTemplates();
    this.modelOptions = this.buildModelOptions(this.records, this.readCatalog());
    this.modelsByMake = this.groupModelsByMake(this.modelOptions);

    this.populateMakes();
    this.setResetState(false);
    this.bindEvents();
    this.restoreFromUrl();
  };

  HarmonizeFitmentGuide.prototype.readRecords = function () {
    var dataScript = this.root.querySelector('[data-fitment-data]');
    if (!dataScript) return [];

    try {
      var payload = JSON.parse(dataScript.textContent || '{}');
      var records = Array.isArray(payload.records) ? payload.records : [];
      return records
        .map(function (record) {
          var startYear = finiteYear(record.startYear);
          var endYear = finiteYear(record.endYear) || startYear;
          if (!startYear || !endYear || !record.productHandle) return null;
          return {
            make: String(record.make || ''),
            makeKey: slugify(record.makeKey || record.make),
            model: String(record.model || ''),
            modelKey: slugify(record.modelKey || record.model),
            startYear: Math.min(startYear, endYear),
            endYear: Math.max(startYear, endYear),
            productHandle: String(record.productHandle),
            productUrl: String(record.productUrl || ''),
            available: record.available !== false,
            collectionHandles: Array.isArray(record.collectionHandles)
              ? record.collectionHandles.map(String)
              : []
          };
        })
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  };

  HarmonizeFitmentGuide.prototype.readCatalog = function () {
    var catalogScript = document.querySelector('#vehicle-compatibility-catalog');
    if (!catalogScript) return [];

    try {
      var payload = JSON.parse(catalogScript.textContent || '{}');
      return Array.isArray(payload.vehicles) ? payload.vehicles : [];
    } catch (error) {
      return [];
    }
  };

  HarmonizeFitmentGuide.prototype.readProductTemplates = function () {
    var templates = new Map();
    this.root.querySelectorAll('[data-fitment-product-template]').forEach(function (template) {
      templates.set(template.dataset.fitmentProductTemplate, template);
    });
    return templates;
  };

  HarmonizeFitmentGuide.prototype.recordMatchesVehicle = function (record, vehicle) {
    if (record.makeKey !== slugify(vehicle.make)) return false;

    var vehicleCollectionKey = collectionKey(vehicle.collectionUrl);
    if (vehicleCollectionKey && record.collectionHandles.indexOf(vehicleCollectionKey) !== -1) return true;

    var vehicleModelKey = slugify(vehicle.model);
    if (record.modelKey === vehicleModelKey) return true;

    if (vehicleModelKey === 'cbr1000rr' && /^cbr1000rr-(sc59|sc77|fireblade)/.test(record.modelKey)) {
      return true;
    }

    if (vehicleModelKey === 'ninja-zx-6r' && /(^|-)zx-6r(-|$)/.test(record.modelKey)) {
      return true;
    }

    return false;
  };

  HarmonizeFitmentGuide.prototype.buildModelOptions = function (records, catalog) {
    var modelOptions = [];
    var catalogRecords = new Set();

    catalog.forEach(function (vehicle) {
      var makeKey = slugify(vehicle.make);
      var modelKey = slugify(vehicle.model);
      var matchingRecords = records.filter(function (record) {
        return this.recordMatchesVehicle(record, vehicle);
      }, this);

      matchingRecords.forEach(function (record) {
        catalogRecords.add(record.productHandle + '|' + record.modelKey);
      });

      modelOptions.push({
        make: String(vehicle.make || ''),
        makeKey: makeKey,
        model: String(vehicle.model || ''),
        modelKey: modelKey,
        collectionUrl: String(vehicle.collectionUrl || ''),
        records: matchingRecords,
        years: unique(
          matchingRecords.reduce(function (years, record) {
            for (var year = record.startYear; year <= record.endYear; year += 1) years.push(year);
            return years;
          }, [])
        ).sort(function (a, b) {
          return a - b;
        })
      });
    }, this);

    records.forEach(function (record) {
      var recordToken = record.productHandle + '|' + record.modelKey;
      if (catalogRecords.has(recordToken)) return;

      var existing = modelOptions.find(function (option) {
        return option.makeKey === record.makeKey && option.modelKey === record.modelKey;
      });
      if (!existing) {
        existing = {
          make: record.make,
          makeKey: record.makeKey,
          model: record.model,
          modelKey: record.modelKey,
          collectionUrl: '',
          records: [],
          years: []
        };
        modelOptions.push(existing);
      }
      existing.records.push(record);
      if (existing.years.indexOf(record.startYear) === -1) existing.years.push(record.startYear);
      if (existing.years.indexOf(record.endYear) === -1) existing.years.push(record.endYear);
      existing.years.sort(function (a, b) {
        return a - b;
      });
    });

    return modelOptions;
  };

  HarmonizeFitmentGuide.prototype.groupModelsByMake = function (modelOptions) {
    var grouped = new Map();
    modelOptions.forEach(function (model) {
      if (!grouped.has(model.makeKey)) grouped.set(model.makeKey, []);
      grouped.get(model.makeKey).push(model);
    });
    return grouped;
  };

  HarmonizeFitmentGuide.prototype.addOption = function (select, value, label) {
    select.appendChild(new Option(label, value));
  };

  HarmonizeFitmentGuide.prototype.resetSelect = function (select, placeholder, disabled) {
    select.innerHTML = '';
    var placeholderOption = new Option(placeholder, '');
    placeholderOption.disabled = true;
    placeholderOption.selected = true;
    select.appendChild(placeholderOption);
    select.value = '';
    select.disabled = disabled;
    select.setAttribute('aria-disabled', String(disabled));
  };

  HarmonizeFitmentGuide.prototype.populateMakes = function () {
    var makes = [];
    var seen = new Set();
    this.modelOptions.forEach(function (model) {
      if (seen.has(model.makeKey)) return;
      seen.add(model.makeKey);
      makes.push({ key: model.makeKey, label: model.make });
    });
    this.resetSelect(this.makeSelect, this.makeSelect.options[0] ? this.makeSelect.options[0].textContent : 'Select make', false);
    makes.forEach(function (make) {
      this.addOption(this.makeSelect, make.key, make.label);
    }, this);
  };

  HarmonizeFitmentGuide.prototype.populateModels = function (makeKey) {
    var placeholder = this.modelSelect.options[0] ? this.modelSelect.options[0].textContent : 'Select model';
    this.resetSelect(this.modelSelect, placeholder, !makeKey);
    if (!makeKey) return;
    (this.modelsByMake.get(makeKey) || []).forEach(function (model) {
      this.addOption(this.modelSelect, model.modelKey, model.model);
    }, this);
    this.modelSelect.disabled = false;
    this.modelSelect.setAttribute('aria-disabled', 'false');
  };

  HarmonizeFitmentGuide.prototype.populateYears = function (model) {
    var placeholder = this.yearSelect.options[0] ? this.yearSelect.options[0].textContent : 'Select year';
    this.resetSelect(this.yearSelect, placeholder, !model || model.years.length === 0);
    if (!model || model.years.length === 0) return;
    model.years.forEach(function (year) {
      this.addOption(this.yearSelect, String(year), String(year));
    }, this);
    this.yearSelect.disabled = false;
    this.yearSelect.setAttribute('aria-disabled', 'false');
  };

  HarmonizeFitmentGuide.prototype.setSubmitState = function (enabled) {
    this.submitButton.disabled = !enabled;
    this.submitButton.setAttribute('aria-disabled', String(!enabled));
  };

  HarmonizeFitmentGuide.prototype.setResetState = function (visible) {
    this.resetButton.hidden = !visible;
    this.resetButton.setAttribute('aria-hidden', String(!visible));
  };

  HarmonizeFitmentGuide.prototype.bindEvents = function () {
    this.form.addEventListener('submit', function (event) {
      event.preventDefault();
    });

    this.makeSelect.addEventListener('change', function () {
      this.selectedModel = null;
      this.populateModels(this.makeSelect.value);
      this.resetSelect(this.yearSelect, this.yearSelect.options[0] ? this.yearSelect.options[0].textContent : 'Select year', true);
      this.setSubmitState(false);
      this.setResetState(Boolean(this.makeSelect.value));
      this.hideResults();
      this.syncUrl({ make: this.makeSelect.value });
      if (this.makeSelect.value) {
        this.track('fitment_make_selected', { make: this.makeSelect.value });
      }
    }.bind(this));

    this.modelSelect.addEventListener('change', function () {
      this.selectedModel = (this.modelsByMake.get(this.makeSelect.value) || []).find(function (model) {
        return model.modelKey === this.modelSelect.value;
      }, this) || null;
      this.populateYears(this.selectedModel);
      this.setSubmitState(false);
      this.setResetState(true);
      this.hideResults();
      this.syncUrl({ make: this.makeSelect.value, model: this.modelSelect.value });
      if (this.selectedModel) {
        this.track('fitment_model_selected', {
          make: this.makeSelect.value,
          model: this.modelSelect.value
        });
      }
    }.bind(this));

    this.yearSelect.addEventListener('change', function () {
      var year = finiteYear(this.yearSelect.value);
      var validYear = year && this.selectedModel && this.selectedModel.years.indexOf(year) !== -1;
      this.setSubmitState(Boolean(validYear));
      if (!validYear) return;
      this.syncUrl({
        make: this.makeSelect.value,
        model: this.modelSelect.value,
        year: String(year)
      });
      this.track('fitment_year_selected', {
        make: this.makeSelect.value,
        model: this.modelSelect.value,
        year: year
      });
      this.renderResults(year);
    }.bind(this));

    this.resetButton.addEventListener('click', function () {
      this.syncUrl({});
      this.restoreFromUrl();
      this.makeSelect.focus();
      this.track('fitment_reset', {});
    }.bind(this));

    this.submitButton.addEventListener('click', function () {
      if (this.submitButton.disabled) return;
      this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }.bind(this));

    this.grid.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      var product = event.target.closest('[data-fitment-product]');
      if (!link || !product || product.dataset.fitmentClicked === 'true') return;
      product.dataset.fitmentClicked = 'true';
      this.track('fitment_product_click', {
        make: this.makeSelect.value,
        model: this.modelSelect.value,
        year: Number(this.yearSelect.value),
        product_handle: product.dataset.fitmentProduct
      });
    }.bind(this));

    window.addEventListener('popstate', function () {
      this.restoreFromUrl();
    }.bind(this));
  };

  HarmonizeFitmentGuide.prototype.restoreFromUrl = function () {
    this.selectedModel = null;
    this.populateMakes();
    this.resetSelect(this.modelSelect, this.modelSelect.options[0] ? this.modelSelect.options[0].textContent : 'Select model', true);
    this.resetSelect(this.yearSelect, this.yearSelect.options[0] ? this.yearSelect.options[0].textContent : 'Select year', true);
    this.setSubmitState(false);
    this.setResetState(false);
    this.hideResults();

    var params = new URLSearchParams(window.location.search);
    var makeKey = slugify(params.get('make'));
    var modelKey = slugify(params.get('model'));
    var year = finiteYear(params.get('year'));
    var makeExists = this.modelsByMake.has(makeKey);
    var model = makeExists
      ? (this.modelsByMake.get(makeKey) || []).find(function (item) { return item.modelKey === modelKey; })
      : null;
    var yearExists = model && year && model.years.indexOf(year) !== -1;

    if (!makeExists) {
      this.syncUrl({}, true);
      return;
    }

    this.makeSelect.value = makeKey;
    this.populateModels(makeKey);
    this.modelSelect.value = model ? model.modelKey : '';
    if (!model) {
      this.setResetState(true);
      this.syncUrl({ make: makeKey }, true);
      return;
    }

    this.selectedModel = model;
    this.populateYears(model);
    this.yearSelect.value = yearExists ? String(year) : '';
    if (!yearExists) {
      this.setResetState(true);
      this.syncUrl({ make: makeKey, model: model.modelKey }, true);
      return;
    }

    this.setResetState(true);
    this.setSubmitState(true);
    this.renderResults(year);
  };

  HarmonizeFitmentGuide.prototype.syncUrl = function (state, replace) {
    var url = new URL(window.location.href);
    ['make', 'model', 'year'].forEach(function (key) {
      url.searchParams.delete(key);
    });
    Object.keys(state).forEach(function (key) {
      if (state[key]) url.searchParams.set(key, state[key]);
    });
    if (url.toString() === window.location.href) return;
    window.history[replace ? 'replaceState' : 'pushState']({}, '', url.toString());
  };

  HarmonizeFitmentGuide.prototype.hideResults = function () {
    this.results.hidden = true;
    this.status.hidden = false;
    this.resultHeading.textContent = '';
    this.resultCount.textContent = '';
    this.grid.innerHTML = '';
    this.empty.hidden = true;
    this.modelLink.hidden = true;
  };

  HarmonizeFitmentGuide.prototype.renderResults = function (year) {
    if (!this.selectedModel) return;
    var matchingRecords = this.selectedModel.records.filter(function (record) {
      return year >= record.startYear && year <= record.endYear;
    });
    var productHandles = unique(matchingRecords.map(function (record) { return record.productHandle; }));
    var resultKey = [this.makeSelect.value, this.modelSelect.value, year].join('|');

    this.results.hidden = false;
    this.status.hidden = true;
    this.resultHeading.textContent = this.results.dataset.headingTemplate
      .replace('__YEAR__', String(year))
      .replace('__MAKE__', this.selectedModel.make)
      .replace('__MODEL__', this.selectedModel.model);
    var countTemplate = productHandles.length === 1
      ? this.results.dataset.countOne
      : this.results.dataset.countOther;
    this.resultCount.textContent = countTemplate.replace('__COUNT__', String(productHandles.length));
    this.grid.innerHTML = '';

    productHandles.forEach(function (handle) {
      var template = this.templateByHandle.get(handle);
      if (!template) return;
      this.grid.appendChild(template.content.cloneNode(true));
    }, this);

    this.empty.hidden = productHandles.length !== 0;
    if (this.selectedModel.collectionUrl) {
      this.modelLink.href = this.selectedModel.collectionUrl;
      this.modelLink.textContent = this.results.dataset.modelLinkTemplate
        .replace('__MAKE__', this.selectedModel.make)
        .replace('__MODEL__', this.selectedModel.model);
      this.modelLink.hidden = false;
    } else {
      this.modelLink.hidden = true;
    }

    if (this.lastResultsKey !== resultKey) {
      this.lastResultsKey = resultKey;
      this.track('fitment_results_viewed', {
        make: this.makeSelect.value,
        model: this.modelSelect.value,
        year: year,
        product_count: productHandles.length
      });
    }
  };

  HarmonizeFitmentGuide.prototype.track = function (eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, payload));
  };

  var init = function (scope) {
    var container = scope || document;
    container.querySelectorAll('[data-fitment-guide]').forEach(function (root) {
      new HarmonizeFitmentGuide(root).init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
